// src/components/support/SupportForm.tsx
import React, { useState } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { Paperclip } from 'lucide-react';
import { FileIcon } from '../data-sources/components/FileIcon';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'application/pdf',
    'text/plain',
    'text/csv',                    // Added CSV MIME type
    'application/csv',             // Alternative CSV MIME type
    'application/vnd.ms-excel',    // Excel files that might contain CSVs
    'application/octet-stream'     // Generic binary (some systems might save CSVs as this)
];

interface SupportFormProps {
    onSubmit: (formData: FormData) => Promise<void>;
}

export const SupportForm: React.FC<SupportFormProps> = ({ onSubmit }) => {
    const [subject, setSubject] = useState('');
    const [description, setDescription] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(e.target.files || []);

        // Special handling for CSV files
        const validFiles = selectedFiles.filter(file => {
            // Check file size first
            if (file.size > MAX_FILE_SIZE) {
                return false;
            }

            // Allow CSV files by checking both MIME type and extension
            if (file.name.toLowerCase().endsWith('.csv')) {
                return true;
            }

            // Check other allowed MIME types
            return ALLOWED_FILE_TYPES.includes(file.type);
        });

        if (validFiles.length !== selectedFiles.length) {
            setError('Some files exceeded size limit or are not supported.');
            return;
        }

        setFiles([...files, ...validFiles]);
        e.target.value = '';
    };

    const removeFile = (index: number) => {
        setFiles(files.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('subject', subject);
            formData.append('description', description);

            files.forEach(file => {
                formData.append('attachments', file);
            });

            await onSubmit(formData);

            setSuccess('Your support request has been submitted successfully.');
            setSubject('');
            setDescription('');
            setFiles([]);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to submit support request');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit}>
            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
                    {success}
                </Alert>
            )}

            <Form.Group className="mb-3">
                <Form.Label className="fw-medium small mb-1">
                    Subject
                    <span className="text-danger ms-1">*</span>
                </Form.Label>
                <Form.Control
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Brief description of your issue"
                    required
                    disabled={isSubmitting}
                    className="border"
                    style={{ height: '38px' }}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-medium small mb-1">
                    Description
                    <span className="text-danger ms-1">*</span>
                </Form.Label>
                <Form.Control
                    as="textarea"
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Please provide as much detail as possible"
                    required
                    disabled={isSubmitting}
                    className="border"
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="fw-medium small mb-0">
                        Attachments
                    </Form.Label>
                    <small className="text-muted">Max size: 10MB per file</small>
                </div>

                <Button
                    variant="outline-secondary"
                    onClick={() => document.getElementById('fileInput')?.click()}
                    disabled={isSubmitting}
                    className="d-flex align-items-center gap-2 mb-3"
                    style={{ height: '38px' }}
                >
                    <Paperclip size={16} />
                    Add Files
                </Button>

                <input
                    type="file"
                    id="fileInput"
                    className="d-none"
                    onChange={handleFileChange}
                    multiple
                    accept={[...ALLOWED_FILE_TYPES, '.csv'].join(',')} // Explicitly add .csv extension
                    disabled={isSubmitting}
                />

                {files.length > 0 && (
                    <div className="border rounded">
                        {files.map((file, index) => (
                            <div
                                key={index}
                                className="p-2 border-bottom d-flex justify-content-between align-items-center"
                            >
                                <div className="d-flex align-items-center">
                                    <FileIcon
                                        mimeType={file.type}
                                        size={20}
                                        className="me-2 text-secondary"
                                    />
                                    <span className="small">{file.name}</span>
                                </div>
                                <Button
                                    variant="link"
                                    className="text-danger p-0"
                                    onClick={() => removeFile(index)}
                                    disabled={isSubmitting}
                                >
                                    ×
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </Form.Group>

            <Button
                type="submit"
                disabled={isSubmitting}
                style={{ height: '38px' }}
            >
                {isSubmitting ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" />
                        Submitting...
                    </>
                ) : (
                    'Submit Request'
                )}
            </Button>
        </Form>
    );
};

export default SupportForm;